import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createBlock,
  type EmailBlock,
  type EmailBlockType,
  htmlToPlainText,
  renderBlockToHtml,
} from '@/lib/emailBlocks';
import {
  emailService,
  EmailPreviewBlock,
  EmailPreviewResult,
  SendEmailPayload,
} from '@/services/email.service';
import { errorMessage } from '@/utils/apiError';
import { QUERY_KEYS } from '@/utils/constants';
import { parseAddressList } from '@/utils/email';

export const useEmailPreview = (
  templateSlug: string,
  leadId: number,
  enabled = true,
  bankLabel?: string,
) =>
  useQuery({
    queryKey: [...QUERY_KEYS.EMAIL_PREVIEW.BY_LEAD(templateSlug, leadId), bankLabel ?? ''],
    queryFn: () => emailService.preview(templateSlug, leadId, bankLabel),
    enabled: enabled && !!templateSlug && !!leadId,
    // Each preview mints no state server-side (placeholder reference number
    // only) but is still a render of the current lead/products - don't let
    // React Query silently serve a stale render across wizard sessions.
    staleTime: 0,
  });

export const useSendEmail = () =>
  useMutation({
    mutationFn: (payload: SendEmailPayload) => emailService.send(payload),
  });

// One item in the composer's combined, re-orderable block list - either one
// of the admin template's locked content blocks (already rendered to final
// HTML server-side, see email.service.ts#previewEmail) or a block the Sales
// RM added themselves (still editable, rendered client-side on the fly).
export type ComposerItem =
  | { kind: 'locked'; id: string; type: string; html: string }
  | { kind: 'rm'; id: string; block: EmailBlock };

// Only these block types are offered to the RM (Fix 2: "give him an option to
// add para, bullet points and table") - the admin-only block types (heading,
// bank details, products table, billing totals, raw HTML) stay admin-only.
export const RM_ADDABLE_BLOCK_TYPES: EmailBlockType[] = ['paragraph', 'list', 'table'];

export interface UseEmailComposerOptions {
  templateSlug: string;
  leadId: number;
  // Gate the preview fetch on having something to render (e.g. products
  // selected) - mirrors the "no products selected" guard both wizard steps
  // already enforce before allowing a send.
  enabled: boolean;
  defaults: { to: string; cc: string; bcc: string; subject: string };
  // StepLeadConfirmation keeps `to` as the lead's own email regardless of the
  // template's defaultTo; StepBillingNotice wants the template's defaultTo
  // (the billing team's address) to win once the preview loads.
  seedTo?: boolean;
  // Chosen company bank account label (ADR-020); changing it re-renders the
  // preview (and re-seeds the locked blocks) with that bank's details.
  bankLabel?: string;
  // RM-chosen From address (a configured Email Sender, or the RM's own email).
  // Envelope-only - does not affect the rendered preview.
  fromEmail?: string;
  // Stable per-step draft identity for the persistence cache. Defaults to
  // templateSlug, but a step whose slug can change for the same logical draft
  // (StepLeadConfirmation flips quotation-draft <-> quotation-draft-discount
  // when a discount is toggled in Step 2) passes a fixed value so the RM's
  // cached edits are not dropped when the slug flips.
  draftKey?: string;
}

// A data-driven block with no data (no additional ARNs, no payment schedule)
// renders to an empty string, and an empty locked card is worse than no card:
// it shows chrome and a type label around nothing. The server already drops
// these (renderEmailPreview), so this is a belt-and-braces guard that also
// covers older/legacy previews.
const lockedItemsFrom = (blocks: EmailPreviewBlock[]): ComposerItem[] =>
  blocks
    .filter((b) => b.html.trim() !== '')
    .map((b) => ({
      kind: 'locked' as const,
      id: b.id,
      type: b.type,
      html: b.html,
    }));

// In-memory snapshot of a composer's EDITABLE state, keyed by templateSlug+leadId.
// Lives at module scope (outside React) so a Sales RM's To/CC/BCC/Subject edits
// and the blocks they added survive the step component unmounting when they
// switch steppers or navigate to another page - cleared only on a full page
// reload, when this module is re-evaluated (deliberately NOT sessionStorage,
// which would also survive a refresh). Only the RM's own edits are cached: the
// locked, data-derived blocks (products table, bank details) and layout are
// always re-rendered from the latest preview so a sent email is never stale, and
// a field the RM has not manually changed keeps following live lead data (the
// `seeded*` baselines below detect "manually edited").
interface ComposerSnapshot {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  seededTo: string;
  seededCc: string;
  seededBcc: string;
  seededSubject: string;
  fieldsSeeded: boolean;
  rmItems: ComposerItem[];
  emailSent: boolean;
}

const composerStateCache = new Map<string, ComposerSnapshot>();

// Shared by every wizard step's email composer (StepLeadConfirmation,
// StepBillingNotice): loads the real rendered preview once per template/lead,
// seeds the editable fields from it exactly once (never overwriting the
// sender's own edits afterwards), lets the RM interleave their own blocks
// with the admin's locked ones, and wraps the send mutation with recipient
// parsing.
export const useEmailComposer = ({
  templateSlug,
  leadId,
  enabled,
  defaults,
  seedTo = false,
  bankLabel,
  fromEmail,
  draftKey,
}: UseEmailComposerOptions) => {
  const emailPreview = useEmailPreview(templateSlug, leadId, enabled, bankLabel);
  const sendEmailMutation = useSendEmail();
  const queryClient = useQueryClient();

  // Restore any in-session draft for this template+lead so edits persist across
  // step switches / page navigation (see composerStateCache). Read once, in the
  // lazy state initializers below. Keyed on the stable draftKey (falling back to
  // templateSlug) so a slug that flips for the same draft doesn't lose edits.
  const cacheKey = `${draftKey ?? templateSlug}:${leadId}`;
  const cached = composerStateCache.get(cacheKey);

  const [to, setTo] = useState(() => cached?.to ?? defaults.to);
  const [cc, setCc] = useState(() => cached?.cc ?? defaults.cc);
  const [bcc, setBcc] = useState(() => cached?.bcc ?? defaults.bcc);
  const [subject, setSubject] = useState(() => cached?.subject ?? defaults.subject);
  // Only RM-added blocks are restored; locked blocks come fresh from the preview.
  const [items, setItems] = useState<ComposerItem[]>(() => cached?.rmItems ?? []);
  const [layoutBefore, setLayoutBefore] = useState('');
  const [layoutAfter, setLayoutAfter] = useState('');
  const [emailSent, setEmailSent] = useState(() => cached?.emailSent ?? false);
  const [fieldsSeeded, setFieldsSeeded] = useState(() => cached?.fieldsSeeded ?? false);
  // The field values as last seeded from the server/lead, so a field the RM has
  // not manually changed keeps following live data while an edited one is kept.
  const [seeded, setSeeded] = useState(() => ({
    to: cached?.seededTo ?? '',
    cc: cached?.seededCc ?? '',
    bcc: cached?.seededBcc ?? '',
    subject: cached?.seededSubject ?? '',
  }));
  const [openPreview, setOpenPreview] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Persist the RM's editable state back to the module cache so it is there when
  // the step remounts. Locked blocks / layout are intentionally NOT cached.
  useEffect(() => {
    composerStateCache.set(cacheKey, {
      to,
      cc,
      bcc,
      subject,
      seededTo: seeded.to,
      seededCc: seeded.cc,
      seededBcc: seeded.bcc,
      seededSubject: seeded.subject,
      fieldsSeeded,
      rmItems: items.filter((i) => i.kind === 'rm'),
      emailSent,
    });
  }, [cacheKey, to, cc, bcc, subject, seeded, fieldsSeeded, items, emailSent]);

  // The latest recipient/subject defaults (derived from the current lead prop),
  // read inside the seed logic without making it depend on a fresh object
  // identity every render.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  // The field values a fresh preview would seed (subject/recipients), matching
  // the original auto-seed semantics.
  const seedTargets = useCallback(
    (data: EmailPreviewResult) => ({
      subject: data.subject,
      to: seedTo && data.defaultTo ? data.defaultTo : defaultsRef.current.to,
      cc: data.defaultCc ?? defaultsRef.current.cc,
      bcc: data.defaultBcc ?? defaultsRef.current.bcc,
    }),
    [seedTo],
  );

  // Applies a rendered preview. Always refreshes the locked blocks + layout from
  // live data (preserving any RM-added blocks, appended after the locked ones).
  // Fields are set on the first seed, on an explicit Refresh (`resetFields`), or
  // when still untouched (current === last-seeded); a manually edited field is
  // left alone so the RM never loses an edit.
  const applyPreview = useCallback(
    (data: EmailPreviewResult, resetFields: boolean): void => {
      const nextLocked = lockedItemsFrom(data.blocks);
      setItems((prev) => {
        // Hard refresh: canonical server order, RM blocks discarded.
        if (resetFields) return nextLocked;
        // Preserve the RM's block ordering ONLY for a live refetch of the SAME
        // template - i.e. the set of locked block ids is unchanged (a bank switch,
        // or a transaction number typed in the billing step: same blocks, new HTML).
        // Any structural change (a template slug flip, or blocks added/removed)
        // falls back to the canonical server order so positional block ids can never
        // map a block onto a differently-typed one.
        const prevLockedIds = prev.filter((i) => i.kind === 'locked').map((i) => i.id);
        const nextLockedIds = nextLocked.map((b) => b.id);
        const sameStructure =
          prevLockedIds.length === nextLockedIds.length &&
          prevLockedIds.every((id, i) => id === nextLockedIds[i]);
        if (!sameStructure) {
          return [...nextLocked, ...prev.filter((i) => i.kind === 'rm')];
        }
        // Same template: update each locked block's HTML in place by id, keeping the
        // RM's interleaved ordering instead of yanking their blocks to the end.
        const lockedById = new Map(nextLocked.map((b) => [b.id, b]));
        return prev.map((item) =>
          item.kind === 'locked' ? (lockedById.get(item.id) ?? item) : item,
        );
      });
      setLayoutBefore(data.layoutBefore);
      setLayoutAfter(data.layoutAfter);

      const target = seedTargets(data);
      const shouldSet = (current: string, wasSeeded: string): boolean =>
        resetFields || !fieldsSeeded || current === wasSeeded;
      if (shouldSet(subject, seeded.subject)) setSubject(target.subject);
      if (shouldSet(to, seeded.to)) setTo(target.to);
      if (shouldSet(cc, seeded.cc)) setCc(target.cc);
      if (shouldSet(bcc, seeded.bcc)) setBcc(target.bcc);
      setSeeded(target);
      setFieldsSeeded(true);
    },
    [seedTargets, fieldsSeeded, subject, to, cc, bcc, seeded],
  );

  useEffect(() => {
    if (!emailPreview.data) return;
    applyPreview(emailPreview.data, false);
    // Only re-run when the rendered preview changes (bank switch, product/lead
    // edit, etc.); applyPreview reads the current field values via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailPreview.data]);

  // Re-fetches the rendered template and re-seeds EVERY composer field from it -
  // To/CC/BCC, subject, and the locked body blocks (products table, totals,
  // bank details) - so the email reflects the latest lead/product/bank data.
  // Seeds straight from the refetch result (not via the effect) so a
  // structurally-equal cached response can't skip the re-seed. Discards any
  // blocks the RM added/reordered, by design.
  const refresh = async (): Promise<void> => {
    const result = await emailPreview.refetch();
    if (!result.data) return;
    applyPreview(result.data, true);
  };

  // Soft refetch: re-renders the locked template blocks from the latest server
  // data (e.g. after a transaction number / mode is recorded) WITHOUT resetting
  // the RM's edited fields or discarding blocks they added. The effect above
  // applies the new preview with resetFields=false when the data changes.
  const refetchPreview = async (): Promise<void> => {
    await emailPreview.refetch();
  };

  const addBlock = (type: EmailBlockType): void => {
    setItems((prev) => [
      ...prev,
      { kind: 'rm', id: crypto.randomUUID(), block: createBlock(type) },
    ]);
  };

  const updateRmBlock = (index: number, block: EmailBlock): void => {
    setItems((prev) =>
      prev.map((item, i) => (i === index && item.kind === 'rm' ? { ...item, block } : item)),
    );
  };

  const removeItem = (index: number): void => {
    // Locked (admin) blocks can be reordered but never removed - the RM can
    // arrange the template, not edit or delete its content.
    setItems((prev) => (prev[index]?.kind === 'rm' ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleDragStart = (index: number): void => setDragIndex(index);
  const handleDragEnd = (): void => setDragIndex(null);
  const handleDragOverIndex = (overIndex: number): void => {
    if (dragIndex === null || dragIndex === overIndex) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
    setDragIndex(overIndex);
  };

  const innerHtml = items
    .map((item) => (item.kind === 'locked' ? item.html : renderBlockToHtml(item.block)))
    .join('\n');
  const bodyHtml = `${layoutBefore}${innerHtml}${layoutAfter}`;

  // True only once the real template's LOCKED content has rendered - before that
  // (still loading, or the preview request failed/404'd because the template was
  // deactivated mid-session) there are no locked blocks and `bodyHtml` would be
  // the bare header/footer (or just a restored RM block) with no template body.
  // Checking for a locked block, not `items.length`, avoids a restored RM-only
  // draft enabling Send before the preview has loaded the locked content.
  const hasRenderedContent = items.some((item) => item.kind === 'locked');
  const canSend = hasRenderedContent && !emailPreview.isError;

  const sendEmailButtonLabel = (): string => {
    if (sendEmailMutation.isPending) return 'Sending…';
    return emailSent ? 'Re-send Email' : 'Send Email';
  };

  const send = (notifyError: (message: string) => void, onSuccess: () => void): void => {
    // Re-checked here (not just left to the UI's disabled state) so a stale
    // button render or a caller that forgets the disabled prop can't ship a
    // blank-content email.
    if (!canSend) {
      notifyError('The email template has not loaded yet. Wait for the preview, then try again.');
      return;
    }
    const toAddresses = parseAddressList(to);
    if (toAddresses.length === 0) {
      notifyError('Recipient email is required');
      return;
    }
    const ccAddresses = parseAddressList(cc);
    const bccAddresses = parseAddressList(bcc);
    sendEmailMutation.mutate(
      {
        templateSlug,
        leadId,
        to: toAddresses,
        cc: ccAddresses.length ? ccAddresses : undefined,
        bcc: bccAddresses.length ? bccAddresses : undefined,
        subject,
        bodyHtml,
        bodyText: htmlToPlainText(bodyHtml),
        fromEmail: fromEmail || undefined,
      },
      {
        onSuccess: () => {
          setEmailSent(true);
          // Refresh the lead's email logs so the "already sent" gate and the
          // recent-emails list reflect this send immediately.
          void queryClient.invalidateQueries({ queryKey: ['email-logs'] });
          onSuccess();
        },
        onError: (error) => {
          // Refetch the email logs so the "already sent" gate and the log list
          // reconcile with real server state after any failure.
          void queryClient.invalidateQueries({ queryKey: ['email-logs'] });
          // A timeout or network drop (no HTTP response) means the send may have
          // completed server-side even though the client never saw the response
          // (a slow link on this heavy PDF + provider request). Steer the RM to
          // verify before retrying so they don't send a duplicate. A real server
          // response (validation/provider rejection) is a genuine failure.
          const noResponse = isAxiosError(error) && !error.response;
          notifyError(
            noResponse
              ? 'Could not confirm the send - your connection may be slow. Check "Emails ' +
                  'sent for this lead" below before retrying to avoid a duplicate.'
              : // A real server response carries the reason (bad sender address,
                // missing configuration, provider rejection). Swallowing it behind a
                // generic "try again" made every one of those failures look identical
                // and gave the RM nothing to act on.
                errorMessage(error, 'Could not send the email. Please try again.'),
          );
        },
      },
    );
  };

  return {
    to,
    setTo,
    cc,
    setCc,
    bcc,
    setBcc,
    subject,
    setSubject,
    items,
    addBlock,
    updateRmBlock,
    removeItem,
    dragIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOverIndex,
    bodyHtml,
    canSend,
    emailSent,
    openPreview,
    setOpenPreview,
    isPreviewLoading: emailPreview.isLoading,
    isPreviewFetching: emailPreview.isFetching,
    isPreviewError: emailPreview.isError,
    isSending: sendEmailMutation.isPending,
    sendEmailButtonLabel,
    send,
    refresh,
    refetchPreview,
  };
};
